import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  usePlmEventResponseByIdQuery,
  useCreatePlmEventResponseMutation,
  useUpdatePlmEventResponseMutation,
  useApprovePlmEventResponseMutation,
  useCancelPlmEventResponseMutation,
  usePlmEventOptionsQuery,
  usePlmCurtailmentPlanOptionsQuery,
  EnumPlmEventResponseResponseStatus,
} from '../../../../types/generated-types';
import {
  Box,
  Typography,
  Grid,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Notifier } from '../../../system/services/notificationManager';
import {
  useInjectableComponents,
  BottomMenuItem,
} from '../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import BorderedSection from '../../shared/borderedSection';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

interface FormValues {
  eventId: string;
  curtailmentPlanId: string;
  scheduledStart: Date | null;
  scheduledEnd: Date | null;
  actualStart: Date | null;
  actualEnd: Date | null;
  responseSentAt: Date | null;
  responseStatus: EnumPlmEventResponseResponseStatus;
}

const validationSchema = Yup.object().shape({
  eventId: Yup.string().required('Event ID is required'),
  curtailmentPlanId: Yup.string().required('Curtailment Plan ID is required'),
  scheduledStart: Yup.date().nullable(),
  scheduledEnd: Yup.date().nullable(),
  actualStart: Yup.date().nullable(),
  actualEnd: Yup.date().nullable(),
  responseSentAt: Yup.date().nullable(),
  responseStatus: Yup.string()
    .oneOf(Object.values(EnumPlmEventResponseResponseStatus))
    .required('Response Status is required'),
});

export const PlmEventResponseDetailForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();
  const isEditMode = !!id;
  const isSmall = useIsSmallScreen();
  const { data: plmEventResponseData, loading: loadingData } =
    usePlmEventResponseByIdQuery({
      variables: { _id: id! },
      skip: !isEditMode,
    });

  const { data: plmEventsData } = usePlmEventOptionsQuery();
  const { data: curtailmentPlansData } = usePlmCurtailmentPlanOptionsQuery();

  const [createPlmEventResponse] = useCreatePlmEventResponseMutation();
  const [updatePlmEventResponse] = useUpdatePlmEventResponseMutation();
  const [approvePlmEventResponse, { loading: approveLoading }] =
    useApprovePlmEventResponseMutation();
  const [cancelPlmEventResponse, { loading: cancelLoading }] =
    useCancelPlmEventResponseMutation();

  const [initialValues, setInitialValues] = useState<FormValues>({
    eventId: '',
    curtailmentPlanId: '',
    scheduledStart: null,
    scheduledEnd: null,
    actualStart: null,
    actualEnd: null,
    responseSentAt: null,
    responseStatus: EnumPlmEventResponseResponseStatus.PendingApproval,
  });

  useEffect(() => {
    if (isEditMode && plmEventResponseData?.plmEventResponseById) {
      const data = plmEventResponseData.plmEventResponseById;
      setInitialValues({
        eventId: data.eventId || '',
        curtailmentPlanId: data.curtailmentPlanId || '',
        scheduledStart: data.scheduledStart
          ? new Date(data.scheduledStart)
          : null,
        scheduledEnd: data.scheduledEnd ? new Date(data.scheduledEnd) : null,
        actualStart: data.actualStart ? new Date(data.actualStart) : null,
        actualEnd: data.actualEnd ? new Date(data.actualEnd) : null,
        responseSentAt: data.responseSentAt
          ? new Date(data.responseSentAt)
          : null,
        responseStatus:
          data.responseStatus ||
          EnumPlmEventResponseResponseStatus.PendingApproval,
      });
    }
  }, [isEditMode, plmEventResponseData]);

  useEffect(() => {
    const title = isEditMode
      ? isSmall
        ? 'Edit PLM Event Response'
        : 'Edit Peak Load Management Event Response'
      : isSmall
        ? 'Add PLM Event Response'
        : 'Create Peak Load Management Event Response';
    const path = isEditMode ? '/plm-event-responses' : '/plm-event-responses';
    setTitle(title);
    setTitlePath(path);
  }, [isEditMode, setTitle, setTitlePath, isSmall]);

  useEffect(() => {
    const menuItems: BottomMenuItem[] = [];

    if (
      can('create', 'PlmEventResponse') ||
      can('update', 'PlmEventResponse')
    ) {
      menuItems.push({
        id: 'save-plm-event-response',
        label: 'Save',
        icon: <SaveIcon />,
        action: () => {
          // This will be handled by the form submission
          const formElement = document.querySelector('form');
          if (formElement) {
            formElement.dispatchEvent(new Event('submit', { bubbles: true }));
          }
        },
      });
    }

    setContextMenuItems([{ items: menuItems }]);
  }, [can, setContextMenuItems]);

  const handleSubmit = async (values: FormValues) => {
    try {
      const input = {
        eventId: values.eventId,
        curtailmentPlanId: values.curtailmentPlanId,
        scheduledStart: values.scheduledStart?.getTime() || null,
        scheduledEnd: values.scheduledEnd?.getTime() || null,
        actualStart: values.actualStart?.getTime() || null,
        actualEnd: values.actualEnd?.getTime() || null,
        responseSentAt: values.responseSentAt?.getTime() || null,
        responseStatus: values.responseStatus,
      };

      if (isEditMode) {
        await updatePlmEventResponse({
          variables: {
            _id: id!,
            plmEventResponse: input,
          },
        });
        Notifier.success('PLM Event Response updated successfully');
      } else {
        await createPlmEventResponse({
          variables: {
            record: input,
          },
        });
        Notifier.success('PLM Event Response created successfully');
      }

      navigate('/plm-event-responses');
    } catch (error) {
      Notifier.error(
        `Failed to ${isEditMode ? 'update' : 'create'} PLM Event Response`,
      );
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      const result = await approvePlmEventResponse({
        variables: { _id: id },
      });

      if (result.data?.approvePlmEventResponse) {
        Notifier.success('PLM Event Response approved successfully');
        // Refresh the data to show updated status
        window.location.reload();
      }
    } catch (error) {
      Notifier.error('Failed to approve PLM Event Response');
      console.error('Approve error:', error);
    }
  };

  const handleCancel = async () => {
    if (!id) return;

    try {
      const result = await cancelPlmEventResponse({
        variables: { _id: id },
      });

      if (result.data?.cancelPlmEventResponse) {
        const newStatus = result.data.cancelPlmEventResponse.responseStatus;
        const actionText = newStatus === 'cancelled' ? 'cancelled' : 'aborted';
        Notifier.success(`PLM Event Response ${actionText} successfully`);
        // Refresh the data to show updated status
        window.location.reload();
      }
    } catch (error) {
      Notifier.error('Failed to cancel/abort PLM Event Response');
      console.error('Cancel/abort error:', error);
    }
  };

  const handleCancelNavigation = () => {
    navigate('/plm-event-responses');
  };

  // Function to calculate scheduled times based on selected event
  const calculateScheduledTimes = (eventId: string) => {
    if (!eventId || !plmEventsData?.plmEvents)
      return { start: null, end: null };

    const selectedEvent = plmEventsData.plmEvents.find(
      (event) => event._id === eventId,
    );
    if (!selectedEvent || !selectedEvent.startTime || !selectedEvent.duration) {
      return { start: null, end: null };
    }

    const startTime = new Date(selectedEvent.startTime);
    const endTime = new Date(
      selectedEvent.startTime + selectedEvent.duration * 60 * 1000,
    ); // Convert minutes to milliseconds

    return { start: startTime, end: endTime };
  };

  if (loadingData) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tooltip title="Back to List">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/plm-event-responses/${id}`)}
              sx={{ mr: 2 }}
            >
              <Typography component="span" sx={{ textTransform: 'none' }}>
                {isEditMode
                  ? isSmall
                    ? 'Edit PLM Event Response'
                    : 'Edit Peak Load Management Event Response'
                  : isSmall
                    ? 'Add PLM Event Response'
                    : 'Create Peak Load Management Event Response'}
              </Typography>
            </Button>
          </Tooltip>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={2}>
                {/* Basic Information */}
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Basic Information">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={!!errors.eventId && touched.eventId}
                        >
                          <InputLabel>Event</InputLabel>
                          <Select
                            name="eventId"
                            value={values.eventId}
                            onChange={(e) => {
                              const selectedEventId = e.target.value;
                              handleChange(e);

                              // Auto-calculate scheduled times when event is selected (only on create)
                              if (!isEditMode && selectedEventId) {
                                const { start, end } =
                                  calculateScheduledTimes(selectedEventId);
                                setFieldValue('scheduledStart', start);
                                setFieldValue('scheduledEnd', end);
                              }
                            }}
                            label="Event"
                          >
                            {plmEventsData?.plmEvents?.map((event) => (
                              <MenuItem key={event._id} value={event._id}>
                                {event._id} -{' '}
                                {new Date(event.startTime).toLocaleString()}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.eventId && touched.eventId && (
                            <Typography variant="caption" color="error">
                              {errors.eventId}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={
                            !!errors.curtailmentPlanId &&
                            touched.curtailmentPlanId
                          }
                        >
                          <InputLabel>Curtailment Plan</InputLabel>
                          <Select
                            name="curtailmentPlanId"
                            value={values.curtailmentPlanId}
                            onChange={handleChange}
                            label="Curtailment Plan"
                          >
                            {curtailmentPlansData?.plmCurtailmentPlans?.map(
                              (plan) => (
                                <MenuItem key={plan._id} value={plan._id}>
                                  {plan.name}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                          {errors.curtailmentPlanId &&
                            touched.curtailmentPlanId && (
                              <Typography variant="caption" color="error">
                                {errors.curtailmentPlanId}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={
                            !!errors.responseStatus && touched.responseStatus
                          }
                        >
                          <InputLabel>Response Status</InputLabel>
                          <Select
                            name="responseStatus"
                            value={values.responseStatus}
                            onChange={handleChange}
                            label="Response Status"
                          >
                            {Object.values(
                              EnumPlmEventResponseResponseStatus,
                            ).map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.responseStatus && touched.responseStatus && (
                            <Typography variant="caption" color="error">
                              {errors.responseStatus}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>

                {/* Scheduled Times */}
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Scheduled Times">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Scheduled Start"
                          value={values.scheduledStart}
                          onChange={(newValue) =>
                            setFieldValue('scheduledStart', newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Scheduled End"
                          value={values.scheduledEnd}
                          onChange={(newValue) =>
                            setFieldValue('scheduledEnd', newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>

                {/* Actual Times - Optional */}
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Actual Times (Optional)">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Actual Start"
                          value={values.actualStart}
                          onChange={(newValue) =>
                            setFieldValue('actualStart', newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              helperText: 'Will be filled in during processing',
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Actual End"
                          value={values.actualEnd}
                          onChange={(newValue) =>
                            setFieldValue('actualEnd', newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              helperText: 'Will be filled in during processing',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>

                {/* Response Information */}
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Response Information">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Response Sent At"
                          value={values.responseSentAt}
                          onChange={(newValue) =>
                            setFieldValue('responseSentAt', newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                  >
                    {/* Approve Button - only show when status is pendingApproval */}
                    {isEditMode &&
                      plmEventResponseData?.plmEventResponseById
                        ?.responseStatus ===
                        EnumPlmEventResponseResponseStatus.PendingApproval && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleApprove}
                          disabled={approveLoading}
                          startIcon={
                            approveLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                        >
                          Approve
                        </Button>
                      )}

                    {/* Cancel/Abort Button - show when status is scheduled or active */}
                    {isEditMode &&
                      plmEventResponseData?.plmEventResponseById
                        ?.responseStatus ===
                        EnumPlmEventResponseResponseStatus.Scheduled && (
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleCancel}
                          disabled={cancelLoading}
                          startIcon={
                            cancelLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CancelIcon />
                            )
                          }
                        >
                          Cancel
                        </Button>
                      )}

                    {isEditMode &&
                      plmEventResponseData?.plmEventResponseById
                        ?.responseStatus ===
                        EnumPlmEventResponseResponseStatus.Active && (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleCancel}
                          disabled={cancelLoading}
                          startIcon={
                            cancelLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CancelIcon />
                            )
                          }
                        >
                          Abort
                        </Button>
                      )}
                    <Button
                      variant="outlined"
                      onClick={handleCancelNavigation}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="secondary"
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                    >
                      {isEditMode ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </LocalizationProvider>
  );
};
